// Updated EmployeeDashboard.jsx with Notification Counter and Checklist Tab
import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import {
  Bell,
  Clock,
  Users,
  ChevronRight,
  Settings,
  LogOut,
  Home,
  BarChart3,
  FileText,
  UserPlus,
  Clipboard,
  Gauge,
  Shield,
  Sun,
  Moon,
  Briefcase,
  X,
  CheckSquare,
  Menu,
  Sparkles,
  ExternalLink,
  ChevronsLeft,
  ChevronsRight,
  Factory,
  ShoppingCart,
  Kanban,
  PanelLeft
} from 'lucide-react';
import NotificationsAnnouncements from './NotificationsAnnouncements';
import DelegationTasks from './DelegationTasks';
import FMSTasks from './FMSTasks';
import HTTasks from './HTTasks';
import PCTasks from './PCTasks';
import HSHelpSlip from './HSHelpSlip';
import AdminNotifications from './AdminNotifications';
import ManagementDashboard from './ManagementDashboard'; // Import the new component
import ChecklistTasks from './ChecklistTasks'; // Import Checklist component
import NewTaskNotification from './NewTaskNotification';
import Overview from './Overview';
import PreProduction from './PreProduction';
import Production from './Production';
import Analytics from './Analytics';
import dataManager from '../utils/DataManager';
import { useNotificationCounter } from '../hooks/useNotificationCounter';
import { useLocalStorage } from '../hooks/useLocalStorage';
// import AIChatBubble from './AIChatBubble'; // disabled — assistant icon hidden
import FollowUpAgent from './FollowUpAgent';

const EmployeeDashboard = ({ currentUser, onLogout, loginTime }) => {
  const { permissions } = currentUser;
  const [selectedTab, setSelectedTab] = useState('overview');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  // Icon-rail collapse, mirroring the PMS sidebar: desktop only, persisted per
  // device, ⌘/Ctrl+B toggles. The mobile drawer always opens full width.
  const [isSidebarCollapsed, setSidebarCollapsed] = useLocalStorage('kairali_sidebar_collapsed', false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(true);
  const rail = isSidebarCollapsed && isDesktopViewport;

  // App layout, persisted per device: 'classic' (left sidebar) or 'board'
  // (AppSheet-style: top bar + bottom nav, Overview becomes the doer board).
  const [layout, setLayout] = useLocalStorage('kairali_layout', 'classic');
  const boardMode = layout === 'board';
  const [openBottomMenu, setOpenBottomMenu] = useState(null);

  // Kept in a ref so the shortcut listener below binds once instead of on
  // every render (useLocalStorage returns a fresh setter each time).
  const setSidebarCollapsedRef = useRef(setSidebarCollapsed);
  setSidebarCollapsedRef.current = setSidebarCollapsed;

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktopViewport(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        setSidebarCollapsedRef.current(v => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [newTaskNotifications, setNewTaskNotifications] = useState([]);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [openTabs, setOpenTabs] = useState([]);
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  
  // Notification counter hook
  const { unreadTodayCount } = useNotificationCounter(currentUser);
  
  // State for pending counts - Updated to include checklist
  const [pendingCounts, setPendingCounts] = useState({
    ht: 0,
    delegation: 0,
    fms: 0,
    pc: 0,
    hs: 0,
    checklist: 0 // Added checklist count
  });

  // Calculate pending counts based on cached data - Updated to include checklist
  const calculatePendingCounts = useCallback(() => {
    const counts = { ht: 0, delegation: 0, fms: 0, pc: 0, hs: 0, checklist: 0 };
      
      try {
        // Get data from cache
        const delegationData = dataManager.getDataWithFallback('delegation', currentUser.name);
        const fmsData = dataManager.getDataWithFallback('fms', currentUser.name);
        const htData = dataManager.getDataWithFallback('ht', currentUser.name);
        const pcData = dataManager.getDataWithFallback('pc', currentUser.name);
        const hsData = dataManager.getDataWithFallback('hs', currentUser.name);

        // Count pending delegation tasks
        if (currentUser.permissions.canViewDelegation) {
          counts.delegation = delegationData.filter(task => 
            (task.delegation_status || 'pending').toLowerCase().includes('pending')
          ).length;
        }

       
     // Count pending FMS tasks (exclude CheckList Task and HelpTicket)
if (currentUser.permissions.canViewFMS) {
  counts.fms = fmsData.filter(task => {
    const fmsType = task.fms || '';
    const isChecklistOrHelpTicket = fmsType.toLowerCase().includes('checklist task') || 
                                    fmsType.toLowerCase().includes('helpticket');
    const delay = parseFloat(task.delay || 0);
    return !isChecklistOrHelpTicket && delay > 0; // Tasks with positive delay are considered pending/overdue
  }).length;
}

        // Count pending PC tasks
        if (currentUser.permissions.canViewPC) {
          counts.pc = pcData.filter(task => {
            const delay = parseFloat(task.delay || 0);
            return delay > 0;
          }).length;
        }

        // Count checklist tasks assigned to current user as Doer
        if (currentUser.permissions.canViewFMS) { // Use same permission as FMS since it's same data source
          const checklistTasks = fmsData.filter(task => {
            // Filter for checklist tasks where current user is the Doer
            const fmsType = task.fms || '';
            const isChecklistTask = fmsType.toLowerCase().includes('checklist task');
            const taskDoer = task.doer || task.assignedTo || '';
            const isUserDoer = taskDoer === currentUser.name;
            
            return isChecklistTask && isUserDoer;
          });
          
          // Count ALL checklist tasks (not just overdue ones)
          counts.checklist = checklistTasks.length;
        }

        // Count pending HT tasks:
        // 1. Delegated TO you — awaiting your reply (no recommendation yet)
        // 2. Raised BY you — resolve planned but not yet done
        if (currentUser.permissions.canViewHT) {
          const pendingReply = htData.filter(task =>
            task.issueDelegatedTo === currentUser.name &&
            (!task.recommendation || task.recommendation.trim() === '')
          ).length;
          const pendingResolve = htData.filter(task =>
            task.name === currentUser.name &&
            task.resolvePlanned?.trim() &&
            (!task.resolveActual || task.resolveActual.trim() === '')
          ).length;
          counts.ht = pendingReply + pendingResolve;
        }

        // Count pending HS tasks
        if (currentUser.permissions.canViewHS) {
          // Check if user is director
          const isDirector = 
            (currentUser?.role?.toLowerCase() === 'director') || 
            (currentUser?.department?.toLowerCase() === 'director');
          
          if (isDirector) {
            // Directors: count all help slips awaiting director's reply
            counts.hs = hsData.filter(task => 
              task.helpSlipId && task.helpSlipId.trim() !== '' &&
              (!task.replyActual || task.replyActual.trim() === '')
            ).length;
          } else {
            // Regular users: count only their own pending tasks
            const userHSData = hsData.filter(task => 
              task.name === currentUser.name || task.assignedTo === currentUser.name
            );
            counts.hs = userHSData.filter(task => 
              task.replyPlanned && task.replyPlanned.trim() !== '' &&
              (!task.replyActual || task.replyActual.trim() === '')
            ).length;
          }
        }

      } catch (error) {
        console.error('Error calculating pending counts:', error);
      }

      return counts;
  }, [currentUser.name, currentUser.permissions, currentUser.role, currentUser.department]);

  // Update pending counts every 30 seconds
  useEffect(() => {
    const updateCounts = () => {
      setPendingCounts(calculatePendingCounts());
    };

    updateCounts();
    const interval = setInterval(updateCounts, 30 * 1000);
    return () => clearInterval(interval);
  }, [calculatePendingCounts]);

  // Setup new task notifications
  useEffect(() => {
    if (currentUser) {
      const handleNewTasks = (notification) => {
        setNewTaskNotifications(prev => {
          const existingIndex = prev.findIndex(n => n.component === notification.component);
          
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              count: updated[existingIndex].count + notification.count,
              newTasks: [...updated[existingIndex].newTasks, ...notification.newTasks]
            };
            return updated;
          } else {
            return [...prev, notification];
          }
        });
        
        setShowNewTaskModal(true);
      };

      dataManager.registerNewTaskCallback(currentUser.name, handleNewTasks);

      return () => {
        dataManager.unregisterNewTaskCallback(currentUser.name);
      };
    }
  }, [currentUser]);

  // Cleanup on logout
  useEffect(() => {
    return () => {
      if (currentUser) {
        dataManager.stopAutoRefresh('delegation', currentUser.name);
        dataManager.stopAutoRefresh('fms', currentUser.name);
        dataManager.stopAutoRefresh('ht', currentUser.name);
        dataManager.stopAutoRefresh('pc', currentUser.name);
        dataManager.stopAutoRefresh('hs', currentUser.name);
      }
    };
  }, [currentUser]);

  const handleCloseNewTaskModal = () => {
    setShowNewTaskModal(false);
  };

  const handleMarkAllRead = () => {
    setNewTaskNotifications([]);
    setShowNewTaskModal(false);
  };

  const handleTabChange = (tabId) => {
    setSelectedTab(tabId);
    setOpenBottomMenu(null);
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const handleMouseEnter = (tabId) => {
    setOpenTabs(prevOpenTabs => {
      if (prevOpenTabs.includes(tabId)) {
        return prevOpenTabs;
      }
      return [...prevOpenTabs, tabId];
    });
  };

  const handleMouseLeave = (tabId) => {
    setOpenTabs(prevOpenTabs => prevOpenTabs.filter(id => id !== tabId));
  };

  // NEW badge — auto-expires after navItem.newUntil date
  const NewBadge = () => (
    <span style={{
      fontSize: 9, fontWeight: 800, letterSpacing: '0.07em',
      padding: '1px 5px', borderRadius: 4, flexShrink: 0,
      background: 'var(--gold-dim)',
      color: 'var(--gold-primary)',
      border: '1px solid var(--gold-primary)',
      textTransform: 'uppercase',
    }}>
      NEW
    </span>
  );

  const AIBadge = () => (
    <span style={{
      fontSize: 9, fontWeight: 800, letterSpacing: '0.07em',
      padding: '1px 5px', borderRadius: 4, flexShrink: 0,
      background: 'rgba(139, 92, 246, 0.15)',
      color: '#a78bfa',
      border: '1px solid #7c3aed',
      textTransform: 'uppercase',
    }}>
      AI
    </span>
  );

  const CountBadge = ({ count }) => {
    if (!count || count === 0) return null;
    const isUrgent  = count > 10;
    const isWarning = count > 5;
    const bg    = isUrgent  ? 'rgba(224,92,92,0.75)'   : isWarning ? 'rgba(212,168,64,0.70)'  : 'rgba(78,189,116,0.65)';
    const border= isUrgent  ? 'rgba(224,92,92,0.35)'   : isWarning ? 'rgba(212,168,64,0.35)'  : 'rgba(78,189,116,0.30)';
    const color = isUrgent  ? '#fff'                   : isWarning ? '#fff'                   : '#fff';
    return (
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 20, height: 20, padding: '0 6px',
          fontSize: 11, fontWeight: 700, borderRadius: 10,
          background: bg, color, border: `1px solid ${border}`,
          letterSpacing: 0,
        }}
        className={isUrgent ? 'badge-pulse' : ''}
      >
        {count}
      </span>
    );
  };

  const NotificationBadge = ({ count }) => {
    if (!count || count === 0) return null;
    return (
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 20, height: 20, padding: '0 6px',
          fontSize: 11, fontWeight: 700, borderRadius: 10,
          background: 'rgba(224,92,92,0.80)',
          color: '#fff',
          border: '1px solid rgba(224,92,92,0.4)',
          letterSpacing: 0,
        }}
        className="badge-pulse"
      >
        {count}
      </span>
    );
  };

  
  // Check if user is management level
const isManagementUser = () => {
  const managementRoles = ['director', 'ceo', 'cto', 'admin', 'ea'];
  const userRole = (currentUser.role || '').toLowerCase().trim();
  return managementRoles.includes(userRole);
};

// Role-based navigation items with counts - Updated to show Management tab only for canViewAdmin permission
const getNavigationItems = (permissions) => {
  const allItems = [
    { 
      id: 'notifications', 
      label: 'Notifications', 
      icon: Bell, 
      permission: 'canViewOverview',
      count: unreadTodayCount,
      isNotification: true
    },
    { 
      id: 'overview', 
      label: 'Overview', 
      icon: Home, 
      permission: 'canViewOverview',
      count: 0
    },
    { 
      id: 'ht-tasks', 
      label: 'HT Tasks', 
      icon: UserPlus, 
      permission: 'canViewHT',
      count: pendingCounts.ht
    },
    { 
      id: 'delegation', 
      label: 'Delegation', 
      icon: Users, 
      permission: 'canViewDelegation',
      count: pendingCounts.delegation
    },
    { 
      id: 'fms', 
      label: 'FMS', 
      icon: FileText, 
      permission: 'canViewFMS',
      count: pendingCounts.fms
    },
    { 
      id: 'pc', 
      label: 'PC Dashboard', 
      icon: Clipboard, 
      permission: 'canViewPC',
      count: pendingCounts.pc
    },
    // Add Checklist tab here
    { 
      id: 'checklist', 
      label: 'Checklist', 
      icon: CheckSquare, 
      permission: 'canViewFMS', // Use same permission as FMS since it's same data source
      count: pendingCounts.checklist
    },
    { 
      id: 'hs', 
      label: 'HelpSlip', 
      icon: UserPlus, 
      permission: 'canViewHS',
      count: pendingCounts.hs
    },
    
    {
      id: 'direct-systems',
      label: 'Direct systems',
      icon: Briefcase,
      permission: 'canViewDirectSystems',
      count: 0,
      subTabs: [
        {
          id: 'pre-production',
          label: 'Pre Production',
          icon: Clipboard,
          permission: 'canViewPreProduction',
        },
        {
          id: 'cms-publish-fms',
          label: 'Content Management System - (Publish FMS)',
          icon: ExternalLink,
          permission: 'canViewDirectSystems',
          externalUrl: 'https://cms.kairali.work/',
        },
        {
          id: 'production-management-system',
          label: 'Production Management System',
          icon: Factory,
          permission: 'canViewDirectSystems',
          externalUrl: 'https://pms.kairali.work',
          newUntil: new Date('2026-10-19'),
        },
        {
          id: 'purchase-management-system',
          label: 'Purchase Management System',
          icon: ShoppingCart,
          permission: 'canViewDirectSystems',
          externalUrl: 'https://purchase.kairali.work',
          newUntil: new Date('2026-10-19'),
        },
        {
          id: 'crm-ktahv-fms',
          label: 'CRM System',
          icon: ExternalLink,
          permission: 'canViewDirectSystems',
          externalUrl: 'https://kairali-group-crm.vercel.app/',
        }
      ]
    },
    
    // Management Dashboard - Only show if user has canViewAdmin permission
    { 
      id: 'management', 
      label: 'Management', 
      icon: Gauge, 
      permission: 'canViewAdmin', // Changed from checking isManagementUser() to using permission
      count: 0,
      special: true
    },
    
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      permission: 'canViewAnalytics',
      count: 0,
      newUntil: new Date('2026-04-04'),
    },
    
    {
      id: 'admin',
      label: 'Admin',
      icon: Settings,
      permission: 'canViewAdmin',
      count: 0
    },

    {
      id: 'aihub',
      label: 'AI Hub',
      icon: Sparkles,
      permission: 'canViewAihub',
      count: 0,
      externalUrl: 'https://kairaliaihub.vercel.app/',
      isAI: true,
    },

  ];

  // Filter items based on permissions - removed special case for management tab
  return allItems.filter(item => {
    return permissions[item.permission];
  });
};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const navigationItems = useMemo(() => getNavigationItems(permissions), [permissions, pendingCounts]);

  // Ensure selected tab is valid for current user
  useEffect(() => {
    const getAllTabIds = (items) => {
      let ids = [];
      items.forEach(item => {
        ids.push(item.id);
        if (item.subTabs) {
          ids = [...ids, ...getAllTabIds(item.subTabs)];
        }
      });
      return ids;
    };

    const validTabs = getAllTabIds(navigationItems);
    if (!validTabs.includes(selectedTab)) {
      setSelectedTab('overview');
    }
  }, [navigationItems, selectedTab]);

  // Keep parent tabs open for selected sub-tabs
  useEffect(() => {
    const findTabHierarchy = (tabId, items, parentPath = []) => {
      for (const item of items) {
        const currentPath = [...parentPath, item.id];
        if (item.id === tabId) {
          return currentPath;
        }
        if (item.subTabs) {
          const subPath = findTabHierarchy(tabId, item.subTabs, currentPath);
          if (subPath) {
            return subPath;
          }
        }
      }
      return null;
    };

    const hierarchy = findTabHierarchy(selectedTab, navigationItems);
    if (hierarchy) {
      const parentIds = hierarchy.slice(0, -1);
      if (parentIds.length > 0) {
        setOpenTabs(prevOpenTabs => {
          const hasAll = parentIds.every(id => prevOpenTabs.includes(id));
          if (hasAll) return prevOpenTabs; // no change — avoid re-render
          return Array.from(new Set([...prevOpenTabs, ...parentIds]));
        });
      }
    }
  }, [selectedTab, navigationItems]);


  const switchLayout = (next) => {
    setLayout(next);
    setOpenBottomMenu(null);
    setSidebarOpen(false);
    // Entering the board lands on it; leaving keeps whatever tab is open.
    if (next === 'board') setSelectedTab('overview');
  };

  // Classic ⇄ Doer board switch. Lives in the sidebar header (classic) and in
  // the top bar (board); both sit on the dark-green sidebar surface.
  const renderLayoutToggle = (compactLabels = false) => (
    <div
      role="tablist"
      aria-label="Dashboard layout"
      className="inline-flex items-center p-0.5 rounded-lg"
      style={{ background: 'rgba(0,0,0,0.22)', border: '1px solid var(--sidebar-border)' }}
    >
      {[
        { id: 'classic', label: 'Classic', Icon: PanelLeft },
        { id: 'board', label: 'Doer board', Icon: Kanban },
      ].map(({ id, label, Icon }) => {
        const active = layout === id;
        return (
          <button
            key={id}
            role="tab"
            aria-selected={active}
            onClick={() => !active && switchLayout(id)}
            title={`${label} layout`}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-colors"
            style={{
              background: active ? 'var(--sidebar-active-bg)' : 'transparent',
              color: active ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)',
              fontWeight: active ? 700 : 500,
            }}
          >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className={compactLabels ? 'hidden sm:inline' : ''}>{label}</span>
          </button>
        );
      })}
    </div>
  );

  // Bottom navigation for the board layout — the sidebar, laid out horizontally.
  const renderBottomBar = () => (
    <nav
      className="kairali-sidebar fixed bottom-0 left-0 right-0 z-40"
      style={{ height: 64, borderRight: 0, borderTop: '1px solid var(--sidebar-border)' }}
      aria-label="Main navigation"
    >
      <div className="h-full flex items-stretch overflow-x-auto px-2" style={{ scrollbarWidth: 'none' }}>
        <div className="flex items-stretch mx-auto">
          {navigationItems.map((item) => {
            const isActive = selectedTab === item.id ||
              (item.subTabs && item.subTabs.some(st => st.id === selectedTab));
            const label = item.id === 'overview' ? 'Pending Task' : item.label;
            const subTabs = item.subTabs ? item.subTabs.filter(st => permissions[st.permission]) : null;
            return (
              <div key={item.id} className="relative flex">
                <button
                  onClick={() => {
                    if (item.externalUrl) {
                      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
                    } else if (subTabs) {
                      setOpenBottomMenu(prev => prev === item.id ? null : item.id);
                    } else {
                      handleTabChange(item.id);
                    }
                  }}
                  title={`${item.label}${item.count ? ` · ${item.count} pending` : ''}`}
                  className="relative flex flex-col items-center justify-center gap-1 px-3 min-w-[76px] transition-colors"
                  style={{
                    color: isActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)',
                    background: isActive ? 'var(--sidebar-active)' : 'transparent',
                    boxShadow: isActive ? 'inset 0 -3px 0 var(--gold-primary)' : 'none',
                  }}
                >
                  <span className="relative flex">
                    <item.icon className="w-[18px] h-[18px]" />
                    {item.count > 0 && (
                      <span
                        className={item.isNotification || item.count > 10 ? 'badge-pulse' : ''}
                        style={{
                          position: 'absolute', top: -7, left: 11,
                          minWidth: 17, height: 17, padding: '0 4px', borderRadius: 9,
                          fontSize: 10, fontWeight: 700, lineHeight: '17px', textAlign: 'center',
                          color: '#fff',
                          background: item.isNotification || item.count > 10
                            ? 'rgba(224,92,92,0.95)'
                            : item.count > 5 ? 'rgba(212,168,64,0.95)' : 'rgba(78,189,116,0.9)',
                        }}
                      >
                        {item.count > 99 ? '99+' : item.count}
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] whitespace-nowrap" style={{ fontWeight: isActive ? 700 : 500 }}>
                    {label}
                  </span>
                </button>

                {/* Grouped items (Direct systems) open upward */}
                {subTabs && openBottomMenu === item.id && (
                  <div
                    className="fixed z-50 rounded-xl py-1.5 shadow-2xl"
                    style={{
                      bottom: 72, minWidth: 260, maxWidth: 'calc(100vw - 16px)',
                      left: 'max(8px, min(var(--menu-left, 8px), calc(100vw - 268px)))',
                      background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    }}
                    ref={el => {
                      if (el && el.parentElement) {
                        const r = el.parentElement.getBoundingClientRect();
                        el.style.setProperty('--menu-left', `${r.left}px`);
                      }
                    }}
                  >
                    {subTabs.map(st => (
                      <button
                        key={st.id}
                        onClick={() => {
                          if (st.externalUrl) {
                            window.open(st.externalUrl, '_blank', 'noopener,noreferrer');
                            setOpenBottomMenu(null);
                          } else {
                            handleTabChange(st.id);
                          }
                        }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-sm transition-colors hover:bg-black/5"
                        style={{ color: selectedTab === st.id ? 'var(--gold-primary)' : 'var(--text-primary)' }}
                      >
                        <st.icon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
                        <span className="flex-1 min-w-0">{st.label}</span>
                        {st.newUntil && new Date() < st.newUntil && <NewBadge />}
                        {st.externalUrl && <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );

  return (
    <div className={`min-h-screen ${boardMode ? 'k-board-layout' : ''}`} style={{ backgroundColor: 'var(--bg-base)' }}>

      {/* Click-away for the bottom bar's upward menu */}
      {boardMode && openBottomMenu && (
        <div className="fixed inset-0 z-30" onClick={() => setOpenBottomMenu(null)} />
      )}

      {!boardMode && (<>
      {/* ── Mobile Overlay ── */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ════════════════════════════════════════
          SIDEBAR
      ════════════════════════════════════════ */}
      <aside className={`
        kairali-sidebar fixed left-0 top-0 h-full z-40 flex flex-col
        transform transition-[transform,width] duration-300 ease-in-out
        ${rail ? 'w-16' : 'w-64'}
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
      `}>

        {/* Collapse toggle — floats on the panel edge (desktop only, ⌘B) */}
        <button
          onClick={() => setSidebarCollapsed(v => !v)}
          title={rail ? 'Expand sidebar (⌘B)' : 'Collapse sidebar (⌘B)'}
          aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
          className="hidden md:flex absolute -right-3 top-[74px] z-50 w-6 h-6 rounded-full items-center justify-center transition-transform duration-200 hover:scale-110"
          style={{
            background: 'linear-gradient(135deg, var(--gold-light), var(--gold-primary))',
            color: '#1a1a1a',
            border: '1px solid var(--sidebar-border)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)'
          }}
        >
          {rail
            ? <ChevronsRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            : <ChevronsLeft className="w-3.5 h-3.5" strokeWidth={2.5} />}
        </button>

        {/* Logo Area */}
        <div className={`pt-7 pb-5 ${rail ? 'px-2' : 'px-5'}`}>
          <div className={`flex items-center ${rail ? 'justify-center' : 'justify-between'}`}>
            <div className={`flex items-center gap-3 min-w-0 ${rail ? 'justify-center' : ''}`}>
              <div className="relative w-10 h-10 rounded-xl overflow-hidden ring-1 ring-gold-500/40 shadow-gold flex-shrink-0">
                <img
                  src="/kairali-logo.png"
                  alt="Kairali"
                  className="w-10 h-10 object-cover"
                  onError={e => {
                    e.target.style.display = 'none';
                    e.target.parentNode.style.background = 'linear-gradient(135deg, #2D6A46, #C8922A)';
                  }}
                />
              </div>
              {!rail && (
                <div className="min-w-0">
                  <h1 className="text-sm font-bold" style={{ color: 'var(--sidebar-text-hov)', letterSpacing: '-0.01em' }}>
                    Kairali Ayurvedic
                  </h1>
                  <p className="text-[10px] font-medium uppercase tracking-widest mt-0.5" style={{ color: 'var(--sidebar-text)' }}>
                    Group
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-1.5 rounded-lg transition-colors hover:bg-forest-700/40"
              style={{ color: 'var(--sidebar-text)' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Gold divider */}
          <div className="mt-4 h-px" style={{ background: 'linear-gradient(90deg, var(--gold-primary), transparent)' }} />

          {/* Layout switch — the rail only has room for the board icon */}
          {rail ? (
            <button
              onClick={() => switchLayout('board')}
              title="Switch to Doer board layout"
              aria-label="Switch to Doer board layout"
              className="kairali-nav-item justify-center px-0 mt-3"
            >
              <Kanban className="w-4 h-4" style={{ color: 'var(--sidebar-text)' }} />
            </button>
          ) : (
            <div className="mt-3">{renderLayoutToggle()}</div>
          )}
        </div>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto space-y-1 pb-4 ${rail ? 'px-2' : 'px-3'}`}>
          {navigationItems.map((item) => {
            const isActive = selectedTab === item.id;
            const isOpen = openTabs.includes(item.id);
            return (
              <div
                key={item.id}
                onMouseEnter={() => item.subTabs && !rail && handleMouseEnter(item.id)}
                onMouseLeave={() => item.subTabs && !rail && handleMouseLeave(item.id)}
              >
                <button
                  onClick={() => {
                    if (item.externalUrl) {
                      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
                    } else if (item.subTabs) {
                      // The submenu has nowhere to render in the rail, so open
                      // the sidebar back up and expand this group.
                      if (rail) {
                        setSidebarCollapsed(false);
                        setOpenTabs(prev => prev.includes(item.id) ? prev : [...prev, item.id]);
                      }
                    } else {
                      handleTabChange(item.id);
                    }
                  }}
                  title={rail ? `${item.label}${item.count ? ` · ${item.count} pending` : ''}` : undefined}
                  className={`kairali-nav-item ${isActive ? 'active' : ''} ${item.special ? 'font-semibold' : ''} ${rail ? 'justify-center px-0' : ''}`}
                >
                  <div className={`flex items-center gap-3 min-w-0 ${rail ? 'justify-center' : ''}`}>
                    <span className="relative flex items-center justify-center flex-shrink-0" style={{ width: 16, height: 16 }}>
                      <item.icon
                        style={{
                          width: 16, height: 16, flexShrink: 0,
                          color: isActive ? 'var(--gold-primary)' : item.special ? 'var(--gold-light)' : 'var(--sidebar-text)',
                        }}
                      />
                      {rail && item.count > 0 && (
                        <span
                          aria-label={`${item.count} pending`}
                          style={{
                            position: 'absolute', top: -4, right: -5,
                            width: 9, height: 9, borderRadius: '50%',
                            background: item.count > 10 ? 'rgba(224,92,92,0.95)' : 'rgba(212,168,64,0.95)',
                            boxShadow: '0 0 0 2px var(--sidebar-bg)',
                          }}
                        />
                      )}
                    </span>
                    {!rail && (
                      <span
                        className="text-sm truncate"
                        style={{
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text-hov)',
                        }}
                      >
                        {item.label}
                      </span>
                    )}
                    {!rail && item.special && (
                      <Shield className="w-3 h-3 text-gold-500/60 flex-shrink-0" />
                    )}
                  </div>
                  <div className={`flex items-center gap-1.5 flex-shrink-0 ${rail ? 'hidden' : ''}`}>
                    {item.isNotification ? (
                      <NotificationBadge count={item.count} />
                    ) : item.subTabs ? (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ background: 'var(--sidebar-border)', color: 'var(--sidebar-text)' }}
                        title={`${item.subTabs.filter(st => permissions[st.permission]).length} modules`}
                      >
                        {item.subTabs.filter(st => permissions[st.permission]).length}
                      </span>
                    ) : (
                      <>
                        <CountBadge count={item.count} />
                        {item.newUntil && new Date() < item.newUntil && <NewBadge />}
                        {item.isAI && <AIBadge />}
                      </>
                    )}
                    {item.subTabs && (
                      <ChevronRight
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                        style={{ color: 'var(--sidebar-text)' }}
                      />
                    )}
                  </div>
                </button>

                {/* Sub-navigation */}
                {item.subTabs && isOpen && !rail && (
                  <div className="ml-3 mt-0.5 pl-3 space-y-0.5 border-l" style={{ borderColor: 'var(--sidebar-border)' }}>
                    {item.subTabs.filter(st => permissions[st.permission]).map(subTab => {
                      const isSubActive = selectedTab === subTab.id;
                      return (
                        <div
                          key={subTab.id}
                          onMouseEnter={() => subTab.subTabs && handleMouseEnter(subTab.id)}
                          onMouseLeave={() => subTab.subTabs && handleMouseLeave(subTab.id)}
                        >
                          <button
                            onClick={() => {
                              if (subTab.externalUrl) {
                                window.open(subTab.externalUrl, '_blank', 'noopener,noreferrer');
                              } else if (!subTab.subTabs) {
                                handleTabChange(subTab.id);
                              }
                            }}
                            className={`kairali-nav-item py-2 ${isSubActive ? 'active' : ''}`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <subTab.icon
                                className="w-3.5 h-3.5 flex-shrink-0"
                                style={{ color: isSubActive ? 'var(--gold-primary)' : 'var(--sidebar-text)' }}
                              />
                              <span
                                className="text-xs font-medium truncate"
                                style={{ color: isSubActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text-hov)' }}
                              >
                                {subTab.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {subTab.newUntil && new Date() < subTab.newUntil && <NewBadge />}
                              {subTab.subTabs && (
                                <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--sidebar-text)' }} />
                              )}
                            </div>
                          </button>
                          {subTab.subTabs && openTabs.includes(subTab.id) && (
                            <div className="ml-3 mt-0.5 pl-3 space-y-0.5 border-l" style={{ borderColor: 'var(--sidebar-border)' }}>
                              {subTab.subTabs.filter(n => permissions[n.permission]).map(nestedTab => (
                                <button
                                  key={nestedTab.id}
                                  onClick={() => handleTabChange(nestedTab.id)}
                                  className={`kairali-nav-item py-2 ${selectedTab === nestedTab.id ? 'active' : ''}`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <nestedTab.icon className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--sidebar-text)' }} />
                                    <span className="text-xs font-medium truncate" style={{ color: 'var(--sidebar-text-hov)' }}>
                                      {nestedTab.label}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── Bottom: User ── */}
        <div className={`pb-4 ${rail ? 'px-2' : 'px-3'}`} style={{ borderTop: '1px solid var(--sidebar-border)' }}>
          {/* Avatar + name row */}
          <button
            onClick={() => setShowRightSidebar(true)}
            title={rail ? `${currentUser.name} · ${currentUser.role}${currentUser.id ? ` · ${currentUser.id}` : ''}` : undefined}
            className={`w-full flex items-center gap-3 py-2.5 mt-3 rounded-xl transition-all hover:bg-forest-800/30 text-left ${rail ? 'justify-center px-0' : 'px-3'}`}
          >
            <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
              style={{ background: 'linear-gradient(135deg, var(--gold-primary), var(--gold-light))', color: '#1a1a1a' }}>
              {currentUser.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            {!rail && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--sidebar-text-hov)' }}>
                  {currentUser.name}
                </p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[10px] truncate" style={{ color: 'var(--sidebar-text)' }}>
                    {currentUser.role}
                  </p>
                  {currentUser.id && (
                    <span className="text-[10px] font-mono tracking-tight flex-shrink-0" style={{ color: 'var(--sidebar-text)', opacity: 0.75 }}>
                      · {currentUser.id}
                    </span>
                  )}
                  {isManagementUser() && (
                    <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                      style={{ background: 'rgba(200, 146, 42, 0.2)', color: 'var(--gold-light)' }}>
                      MGMT
                    </span>
                  )}
                </div>
              </div>
            )}
          </button>

          {/* Action row: theme toggle + logout */}
          <div className={`flex items-center mt-1 ${rail ? 'flex-col gap-1 px-0' : 'gap-2 px-3'}`}>
            <button
              onClick={toggleTheme}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors hover:bg-forest-800/40"
              style={{ color: 'var(--gold-primary)' }}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode
                ? <><Sun className="w-3.5 h-3.5 flex-shrink-0" />{!rail && <span className="text-[11px] font-medium" style={{ color: 'var(--sidebar-text)' }}>Light</span>}</>
                : <><Moon className="w-3.5 h-3.5 flex-shrink-0" />{!rail && <span className="text-[11px] font-medium" style={{ color: 'var(--sidebar-text)' }}>Dark</span>}</>
              }
            </button>
            {!rail && <div style={{ width: 1, height: 20, background: 'var(--sidebar-border)' }} />}
            <button
              onClick={onLogout}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors hover:bg-red-500/15"
              style={{ color: 'var(--sidebar-text)' }}
              title="Sign out"
            >
              <LogOut className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#F87171' }} />
              {!rail && <span className="text-[11px] font-medium" style={{ color: '#F87171' }}>Sign out</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile hamburger — floating, only on small screens */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 p-2 rounded-xl shadow-lg"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          color: 'var(--text-secondary)',
        }}
        aria-label="Open menu"
      >
        <Menu className="w-4 h-4" />
      </button>
      </>)}

      {/* ════════════════════════════════════════
          MAIN CONTENT
      ════════════════════════════════════════ */}
      <div className={`flex flex-col min-h-screen transition-[margin] duration-300 ease-in-out ${boardMode ? '' : rail ? 'md:ml-16' : 'md:ml-64'}`}>

        {/* ── Board layout top bar ── */}
        {boardMode && (
          <header
            className="kairali-sidebar sticky top-0 z-30 flex items-center gap-3 px-3 sm:px-4"
            style={{ height: 56, borderRight: 0, borderBottom: '1px solid var(--sidebar-border)' }}
          >
            <div className="w-8 h-8 rounded-lg overflow-hidden ring-1 ring-gold-500/40 flex-shrink-0">
              <img
                src="/kairali-logo.png"
                alt="Kairali"
                className="w-8 h-8 object-cover"
                onError={e => {
                  e.target.style.display = 'none';
                  e.target.parentNode.style.background = 'linear-gradient(135deg, #2D6A46, #C8922A)';
                }}
              />
            </div>
            <h1 className="hidden md:block text-sm font-bold truncate" style={{ color: 'var(--sidebar-text-hov)' }}>
              Kairali Ayurvedic
            </h1>
            <div className="flex-1 flex justify-center min-w-0">{renderLayoutToggle(true)}</div>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg transition-colors hover:bg-forest-800/40 flex-shrink-0"
              style={{ color: 'var(--gold-primary)' }}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setShowRightSidebar(true)}
              title={`${currentUser.name} · ${currentUser.role}`}
              aria-label="Open profile"
              className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
              style={{ background: 'linear-gradient(135deg, var(--gold-primary), var(--gold-light))', color: '#1a1a1a' }}
            >
              {currentUser.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
            </button>
          </header>
        )}

        {/* ── Page Content ── */}
        <main className={`flex-1 ${boardMode ? 'p-3 sm:p-4' : 'p-4 sm:p-6'}`} style={boardMode ? { paddingBottom: 76 } : undefined}>
          {selectedTab === 'notifications' && (
            <NotificationsAnnouncements currentUser={currentUser} />
          )}
          {selectedTab === 'overview' && (
            <Overview currentUser={currentUser} onTabChange={handleTabChange} boardMode={boardMode} />
          )}
          {selectedTab === 'delegation' && currentUser.permissions.canViewDelegation && (
            <DelegationTasks currentUser={currentUser} />
          )}
          {selectedTab === 'ht-tasks' && currentUser.permissions.canViewHT && (
            <HTTasks currentUser={currentUser} />
          )}
          {selectedTab === 'fms' && currentUser.permissions.canViewFMS && (
            <FMSTasks currentUser={currentUser} />
          )}
          {selectedTab === 'pc' && currentUser.permissions.canViewPC && (
            <PCTasks currentUser={currentUser} />
          )}
          {selectedTab === 'checklist' && currentUser.permissions.canViewFMS && (
            <ChecklistTasks currentUser={currentUser} />
          )}
          {selectedTab === 'hs' && currentUser.permissions.canViewHS && (
            <HSHelpSlip currentUser={currentUser} />
          )}
          {selectedTab === 'analytics' && currentUser.permissions.canViewAnalytics && (
            <Analytics currentUser={currentUser} />
          )}
          {selectedTab === 'management' && currentUser.permissions.canViewAdmin && (
            <ManagementDashboard currentUser={currentUser} />
          )}
          {selectedTab === 'admin' && currentUser.permissions.canViewAdmin && (
            <AdminNotifications currentUser={currentUser} />
          )}
          {selectedTab === 'pre-production' && currentUser.permissions.canViewPreProduction && (
            <PreProduction currentUser={currentUser} />
          )}
          {(selectedTab === 'production' || selectedTab === 'ims-update') && currentUser.permissions.canViewProduction && (
            <Production currentUser={currentUser} />
          )}
        </main>
      </div>

      {boardMode && renderBottomBar()}

      {/* ── AI Chat Bubble (disabled — floating assistant icon hidden) ── */}
      {/* <AIChatBubble currentUser={currentUser} selectedTab={selectedTab} /> */}

      {/* ── Smart Follow-up Agent (11am–5pm IST) ── */}
      <FollowUpAgent currentUser={currentUser} />

      {/* ── New Task Modal ── */}
      {showNewTaskModal && (
        <NewTaskNotification
          notifications={newTaskNotifications}
          onClose={handleCloseNewTaskModal}
          onMarkAllRead={handleMarkAllRead}
        />
      )}

      {/* ════════════════════════════════════════
          RIGHT SIDEBAR — Profile Panel
      ════════════════════════════════════════ */}
      {showRightSidebar && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={e => e.target === e.currentTarget && setShowRightSidebar(false)}
        >
          <div
            className="w-80 h-full flex flex-col shadow-2xl overflow-hidden animate-slide-in"
            style={{
              background: 'var(--bg-surface)',
              borderLeft: '1px solid var(--border)',
            }}
          >
            {/* Profile header */}
            <div className="relative px-6 pt-8 pb-6 text-center"
              style={{ background: 'linear-gradient(180deg, var(--bg-surface-3) 0%, var(--bg-surface) 100%)', borderBottom: '1px solid var(--border)' }}>
              <button
                onClick={() => setShowRightSidebar(false)}
                className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-colors"
                style={{ color: 'var(--sidebar-text)' }}
              >
                <X className="w-4 h-4" />
              </button>

              {/* Avatar */}
              <div className="relative inline-block mb-4">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold shadow-gold mx-auto"
                  style={{
                    background: 'linear-gradient(135deg, var(--gold-primary), var(--gold-light))',
                    color: '#1A2E23',
                  }}>
                  {currentUser.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <span className="absolute -bottom-1.5 -right-1.5 w-5 h-5 rounded-full bg-forest-400 border-2 flex items-center justify-center"
                  style={{ borderColor: 'var(--sidebar-bg)' }}>
                  <span className="w-2 h-2 rounded-full bg-white" />
                </span>
              </div>

              <h3 className="text-lg font-bold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                {currentUser.name}
              </h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {currentUser.email}
              </p>
            </div>

            {/* Info */}
            <div className="flex-1 p-5 space-y-3 overflow-y-auto" style={{ background: 'var(--bg-surface-2)' }}>
              <div className="k-card p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(45, 106, 70, 0.1)' }}>
                  <Briefcase className="w-4 h-4" style={{ color: 'var(--green-primary)' }} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--text-muted)' }}>Role</p>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{currentUser.role}</p>
                </div>
              </div>

              <div className="k-card p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(200, 146, 42, 0.1)' }}>
                  <Clock className="w-4 h-4" style={{ color: 'var(--gold-primary)' }} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--text-muted)' }}>Session started</p>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {new Date(parseInt(loginTime, 10)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              <div className="k-card p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(26, 107, 124, 0.1)' }}>
                  <Settings className="w-4 h-4" style={{ color: 'var(--teal-primary)' }} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--text-muted)' }}>Department</p>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{currentUser.department || '—'}</p>
                </div>
              </div>
            </div>

            {/* Sign out */}
            <div className="p-4" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
              <button
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, var(--c-danger), #e05c5c)',
                  color: 'var(--text-inverted)',
                  boxShadow: '0 4px 16px rgba(184,50,50,0.30)',
                }}
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;
